import type { FraisDTO } from '@/features/api/extranet/dto/Demande/shared/FraisDTO.ts'
import type { DateRange } from '@/features/api/extranet/dto/shared/DateRangeDTO.ts'
import { type TypeContextCourant, TypesContextesAutorisesEnum } from '@/features/auth/context-app/types/ContextApp'
import { isNullUndefinedOrEmptyString, toISODateOnlyString } from '@/features/shared/tools'
import { type Region, Sexe } from '../../types/opco'
import { SmicPeriodes } from './constantes'
import type { DateOnly } from '@/features/domaine/types/techniques'
import { plainDateFromDateOnly } from '@/features/shared/tools/dateOnly.ts'

export function isAdherentOuMandatGestion(
  typeContext: TypeContextCourant,
  sousMandat: boolean | null | undefined,
): boolean {
  return (
    typeContext === TypesContextesAutorisesEnum.Adherent ||
    (typeContext === TypesContextesAutorisesEnum.Prestataire && sousMandat === true)
  )
}

export function checkNir(nir: string): boolean {
  return !!nir.match(
    '((^([1-2])([0-9]{2})(0[1-9]|[2-35-9][0-9]|[14][0-2])((0[1-9]|[1-9][0-9]|2[abAB])(00[1-9]|0[1-9][0-9]|[1-8][0-9]{2}|9[0-8][0-9]|990)|(9[78][0-9])(0[1-9]|[1-8][0-9]|90))(00[1-9]|0[1-9][0-9]|[1-9][0-9]{2})([0-9]?)([0-9]?)$)|(^([3-47-8])([0-9]{12})([0-9]?)([0-9]?)$))',
  )
}

/**
 * Vérifie si le NIR est un NIA (Numéro d'Identification d'Attente)
 * Les NIA commencent par 3, 4, 7 ou 8
 */
export function isNia(nir: string): boolean {
  if (!nir || nir.length === 0) return false
  const firstChar = nir.charAt(0)
  return firstChar === '3' || firstChar === '4' || firstChar === '7' || firstChar === '8'
}

export function checkNirTemporaire(nir: string): boolean {
  return !!nir.match('^[\\w]{11,40}$')
}

export function checkNirSexe(nir: string, sexe: Sexe): boolean {
  if (!checkNir(nir)) return true
  if (isNia(nir)) return true

  let sexeValue = '1'
  if (sexe === 'Homme') sexeValue = '1'

  if (sexe === 'Femme') sexeValue = '2'

  const sexeNir = nir.substring(0, 1)
  return sexeValue === sexeNir
}

export function checkNirDateNaissance(nir: string, dateNaissance: DateOnly): boolean {
  if (!checkNir(nir)) return true
  if (isNia(nir)) return true

  const yearNir = nir.substring(1, 3)
  const monthNir = Number(nir.substring(3, 5))

  const plainDateNaissance = plainDateFromDateOnly(dateNaissance)
  if (!plainDateNaissance) return true

  if (!plainDateNaissance.year.toString().endsWith(yearNir)) return false

  if (monthNir <= 12) {
    if (plainDateNaissance.month !== monthNir) {
      return false
    }
  }

  return true
}

export function checkNirDepartement(nir: string, departement: string): boolean {
  if (!checkNir(nir)) return true
  if (isNia(nir)) return true

  const nbCaractere = 2
  const indexEnd = 7
  departement = departement.padStart(nbCaractere, '0').toUpperCase()
  let departementNir = nir.substring(5, indexEnd).toUpperCase()

  if (isDrom(departement)) {
    // Cas particulier de Mayotte
    if (departementNir === '98' && departement === '976') departementNir = '97'
    departement = departement.substring(0, 2)
  }

  return departement === departementNir
}

export function isDrom(departement: string): boolean {
  if (isNullUndefinedOrEmptyString(departement)) return false

  return departement.startsWith('97') || departement.startsWith('98')
}

export function getRangeDate(dateDebut: Date | null, dateFin: Date | null): DateRange {
  return { start: toISODateOnlyString(dateDebut), end: toISODateOnlyString(dateFin) }
}

export function sumMontantFrais(listeFrais: FraisDTO[]): number {
  return (
    listeFrais.reduce((total, frais) => {
      if (typeof frais.montant !== 'number') return total

      if (frais.baseCalcul === 'Quantite') {
        if (typeof frais.quantite === 'number') {
          total += frais.montant * frais.quantite * 100
        }
      } else {
        total += frais.montant * 100
      }

      return total
    }, 0) / 100
  )
}

export function calculSalaire(tauxHoraireBrut: number, tauxCharge: number, dureeFormation: number): number {
  return dureeFormation * tauxHoraireBrut * (1 + tauxCharge)
}

/**
 * Méthode permettant de trouver le SMIC en fonction d'une date référence
 * @param region : 'METROPOLE' ou 'MAYOTTE'
 * @param dateReference : date de référence pour retrouve le SMIC
 * @returns
 */
export function getSmicHoraireBrut(region: Region, dateReference: Date = new Date()): number {
  const periode = SmicPeriodes.find((p) => dateReference >= p.debut && (p.fin === null || dateReference <= p.fin))

  if (!periode) {
    throw new Error('Aucun taux trouvé pour cette date.')
  }

  return region === 'METROPOLE' ? periode.tauxMetropole : periode.tauxMayotte
}
